import type { HeroFragment } from '#lib/features/hero/queries/__generated__/fragments.generated.js';
import { getDirectusDateTimestamp } from '$global/utils/getDirectusDateTimestamp';

type HeroDropButtons = Pick<
	HeroFragment,
	| 'button_1_label'
	| 'button_1_link'
	| 'button_2_label'
	| 'button_2_link'
	| 'inactive_button_1_label'
	| 'inactive_button_1_link'
	| 'inactive_button_2_label'
	| 'inactive_button_2_link'
> & {
	drop_config?: { checkout_end_date?: string | null } | null;
};

/**
 * Once the hero's drop has closed, swaps the hero's buttons for its inactive
 * buttons, provided at least one inactive button is complete. The whole set
 * is swapped so an active button never sits next to an inactive one.
 *
 * `now` is the time context's server time, null until it has been fetched:
 * the home pages are ISR-cached, so the drop status is only worked out in the
 * browser. Until then, a hero that could swap shows no buttons rather than
 * flashing its active ones; a hero without inactive buttons shows its active
 * ones straight away.
 */
export const getHeroWithDropButtons = <T extends HeroDropButtons>(
	hero: T,
	now: number | null
): T => {
	const checkoutEndDate = hero.drop_config?.checkout_end_date;

	const hasInactiveButton =
		!!(hero.inactive_button_1_label && hero.inactive_button_1_link) ||
		!!(hero.inactive_button_2_label && hero.inactive_button_2_link);

	if (!checkoutEndDate || !hasInactiveButton) {
		return hero;
	}

	if (now === null) {
		return {
			...hero,
			button_1_label: null,
			button_1_link: null,
			button_2_label: null,
			button_2_link: null,
		};
	}

	if (now < getDirectusDateTimestamp(checkoutEndDate)) {
		return hero;
	}

	return {
		...hero,
		button_1_label: hero.inactive_button_1_label,
		button_1_link: hero.inactive_button_1_link,
		button_2_label: hero.inactive_button_2_label,
		button_2_link: hero.inactive_button_2_link,
	};
};
